(function () {
  'use strict';
  const text = value => String(value == null ? '' : value).trim();
  const norm = value => text(value).toUpperCase().replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\s+/g, ' ');
  const validRow = row => row && text(row.make) && text(row.model) && text(row.engine) && Number.isInteger(Number(row.from)) && Number.isInteger(Number(row.to)) && Number(row.from) >= 1900 && Number(row.to) >= Number(row.from) && Number(row.to) <= 2100;
  const matches = (row, query, mode) => {
    if (!validRow(row) || !text(query.engine) || norm(row.engine) !== norm(query.engine)) return false;
    if (mode === 'engine') return true;
    const year = Number(query.year);
    return !!text(query.make) && !!text(query.model) && Number.isInteger(year) && norm(row.make) === norm(query.make) && norm(row.model) === norm(query.model) && year >= Number(row.from) && year <= Number(row.to);
  };
  const find = (products, query, mode) => products.filter(product => product.rows.some(row => matches(row, query, mode)));
  const categoryOptions = (products, definitions) => {
    const counts = new Map(definitions.map(category => [category.handle, 0]));
    products.forEach(product => {
      new Set(product.categories || []).forEach(handle => {
        if (counts.has(handle)) counts.set(handle, counts.get(handle) + 1);
      });
    });
    return definitions.filter(category => counts.get(category.handle) > 0).map(category => ({...category, count: counts.get(category.handle)}));
  };
  const categorySelection = value => [...new Set((Array.isArray(value) ? value : [value]).flatMap(item => text(item).split(',')).map(text).filter(Boolean))].slice(0, 50);
  const filterCategory = (products, value, definitions) => {
    const selected = categorySelection(value);
    if (!selected.length) return products;
    const allowed = new Set(definitions.map(category => category.handle));
    const handles = selected.filter(handle => allowed.has(handle));
    return products.filter(product => Array.isArray(product.categories) && handles.some(handle => product.categories.includes(handle)));
  };
  const validCategories = product => Array.isArray(product.categories) && product.categories.every(handle => typeof handle === 'string' && handle.length > 0);
  const values = (rows, field) => [...new Set(rows.map(row => text(row[field])).filter(Boolean))].sort((a, b) => {
    if (field === 'make') {
      if (norm(a) === 'ISUZU' && norm(b) !== 'ISUZU') return -1;
      if (norm(b) === 'ISUZU' && norm(a) !== 'ISUZU') return 1;
    }
    return a.localeCompare(b, 'en', {numeric: true});
  });
  const vinKind = value => {
    const vin = norm(value).replace(/\s+/g, '');
    if (/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) return {kind: 'vin', vin};
    if (/^[A-Z0-9-]{5,16}$/.test(vin)) return {kind: 'chassis', vin};
    return {kind: 'invalid', vin};
  };
  const decodeFields = data => {
    if (!data || !Array.isArray(data.Results)) throw new Error('Invalid VIN response');
    const fields = Object.fromEntries(data.Results.map(field => [field.Variable, text(field.Value)]));
    return {make: fields.Make || '', model: fields.Model || '', year: fields['Model Year'] || '', engine: fields['Engine Model'] || '', liters: fields['Displacement (L)'] || '', fuel: fields['Fuel Type - Primary'] || '', error: fields['Error Code'] || ''};
  };
  const core = {norm, validRow, matches, find, values, vinKind, decodeFields, categoryOptions, filterCategory, categorySelection, validCategories};
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('gt-vehicle-finder')) return;

  async function readCatalogCache(key) {
    const saved = sessionStorage.getItem(key);
    if (!saved) return null;
    if (!saved.startsWith('gz:')) return JSON.parse(saved);
    const bytes = Uint8Array.from(atob(saved.slice(3)), char => char.charCodeAt(0));
    return JSON.parse(await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text());
  }
  async function writeCatalogCache(key, value) {
    try {
      const json = JSON.stringify(value);
      if (typeof CompressionStream === 'undefined') {sessionStorage.setItem(key, json); return;}
      const bytes = new Uint8Array(await new Response(new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'))).arrayBuffer());
      let binary = '';
      for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
      sessionStorage.setItem(key, 'gz:' + btoa(binary));
    } catch (error) {}
  }

  let catalogPromise;
  async function loadCatalog(source, progress, context) {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const cacheKey = 'gt-finder-catalog:v4:' + context;
      try {
        const staleKeys = [];
        for (let index = 0; index < sessionStorage.length; index++) {
          const key = sessionStorage.key(index);
          if (key && key.startsWith('gt-finder-catalog:') && key !== cacheKey) staleKeys.push(key);
        }
        staleKeys.forEach(key => sessionStorage.removeItem(key));
        const cached = await readCatalogCache(cacheKey);
        if (cached && cached.expires > Date.now() && cached.expires <= Date.now() + 120000 && Array.isArray(cached.products) && cached.products.every(product => validCategories(product) && typeof product.card === 'string' && product.card.includes('product-item__info-inner') && Array.isArray(product.rows) && product.rows.length === product.expectedRows && text(product.url).startsWith('/'))) return {products: cached.products, rows: cached.products.flatMap(product => product.rows).filter(validRow), total: cached.total};
      } catch (error) {}
      const requestPage = async page => {
        const url = new URL(source, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid catalog origin');
        url.searchParams.set('view', 'gt-fitment-data');
        url.searchParams.set('page', String(page));
        url.searchParams.set('sort_by', 'created-ascending');
        const response = await fetch(url, {credentials: 'same-origin', signal: AbortSignal.timeout(25000)});
        if (!response.ok) throw new Error('Catalog unavailable');
        const data = await response.json();
        if (data.version !== 3 || typeof data.categoryConfig !== 'string' || data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 250 || !Number.isInteger(data.total) || data.total > 25000 || !Number.isInteger(data.scanned)) throw new Error('Invalid catalog page');
        data.products.forEach(product => {
          if (!Array.isArray(product.rows) || product.rows.some(row => !Array.isArray(row) || row.length !== 5)) throw new Error('Invalid fitment rows');
          product.rows = product.rows.map(([make, model, from, to, engine]) => ({make, model, from, to, engine}));
        });
        return data;
      };
      const first = await requestPage(1);
      const pages = [first];
      progress(1, first.pages);
      let nextPage = 2;
      await Promise.all(Array.from({length: Math.min(12, Math.max(0, first.pages - 1))}, async () => {
        while (nextPage <= first.pages) {
          const page = nextPage++;
          pages[page - 1] = await requestPage(page);
          progress(pages.filter(Boolean).length, first.pages);
        }
      }));
      if (pages.some(page => page.total !== first.total || page.pages !== first.pages || page.categoryConfig !== first.categoryConfig) || pages.reduce((sum, page) => sum + page.scanned, 0) !== first.total) throw new Error('Catalog changed during loading');
      const products = pages.flatMap(page => page.products);
      const ids = new Set();
      for (const product of products) {
        if (ids.has(product.id) || !validCategories(product) || typeof product.card !== 'string' || !product.card.includes('product-item__info-inner') || !Array.isArray(product.rows) || product.rows.length !== product.expectedRows || !text(product.url).startsWith('/')) throw new Error('Incomplete catalog');
        ids.add(product.id);
      }
      await writeCatalogCache(cacheKey, {expires: Date.now() + 120000, products, total: first.total});
      return {products, rows: products.flatMap(product => product.rows).filter(validRow), total: first.total};
    })().catch(error => {catalogPromise = null; throw error;});
    return catalogPromise;
  }

  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.full = this.dataset.full === 'true';
      this.compact = this.dataset.compact === 'true';
      this.mode = 'vehicle';
      this.catalog = null;
      this.form = this.querySelector('[data-vehicle-form]');
      this.selects = Object.fromEntries(['make', 'model', 'year', 'engine'].map(name => [name, this.form.elements.namedItem(name)]));
      this.submitButton = this.form.querySelector('[type=submit]');
      this.status = this.querySelector('[data-status]');
      this.loadButton = this.querySelector('[data-load]');
      this.results = this.querySelector('.gt-finder__results');
      this.categoryDefinitions = JSON.parse(this.querySelector('[data-categories]')?.textContent || '[]');
      this.categoryList = this.querySelector('[data-category-list]');
      this.filterPanel = this.querySelector('[data-category-filter]');
      this.pendingCategory = null;
      this.categoryList?.addEventListener('change', () => this.filterResults());
      this.querySelector('[data-clear-category]')?.addEventListener('click', () => {
        this.categoryList.querySelectorAll('input').forEach(input => {input.checked = false;});
        this.filterResults();
        this.categoryList.querySelector('input')?.focus();
      });
      this.querySelector('[data-active-categories]')?.addEventListener('click', event => {
        const button = event.target.closest('[data-remove-category]');
        if (!button) return;
        const input = [...this.categoryList.querySelectorAll('input')].find(choice => choice.value === button.dataset.removeCategory);
        if (input) input.checked = false;
        this.filterResults();
        this.querySelector('[data-result-title]').focus({preventScroll: true});
      });
      this.querySelector('[data-open-filters]')?.addEventListener('click', () => this.openFilters());
      this.querySelectorAll('[data-close-filters],[data-apply-filters],[data-filter-overlay]').forEach(button => button.addEventListener('click', () => this.closeFilters()));
      this.filterPanel?.addEventListener('keydown', event => this.filterKeydown(event));
      if (this.filterPanel) {
        this.filterMedia = window.matchMedia('(max-width: 999px)');
        this.filterMediaChanged = () => {this.closeFilters(false); this.syncFilterPanel();};
        this.filterMedia.addEventListener('change', this.filterMediaChanged);
        this.syncFilterPanel();
      }
      this.querySelectorAll('[data-mode]').forEach(button => button.addEventListener('click', () => this.setMode(button.dataset.mode)));
      this.loadButton.addEventListener('click', () => this.ensureCatalog());
      this.form.addEventListener('submit', event => {event.preventDefault(); this.search();});
      ['make', 'model', 'year', 'engine'].forEach(name => this.selects[name].addEventListener('change', () => this.updateSelectors(name)));
      this.querySelector('[data-vin-form]')?.addEventListener('submit', event => {event.preventDefault(); this.decodeVin();});
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.renderResults(this.resultPage - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.renderResults(this.resultPage + 1));
      if (this.full) this.ensureCatalog().then(() => this.restoreQuery());
      else if (this.compact) {
        const start = () => {
          this.catalogObserver?.disconnect();
          this.ensureCatalog();
        };
        this.addEventListener('pointerenter', start, {once: true});
        this.addEventListener('focusin', start, {once: true});
        if ('IntersectionObserver' in window) {
          this.catalogObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) start();
          }, {rootMargin: '200px'});
          this.catalogObserver.observe(this);
        } else start();
      }
    }
    disconnectedCallback() {
      this.catalogObserver?.disconnect();
      this.closeFilters(false);
      this.filterMedia?.removeEventListener('change', this.filterMediaChanged);
    }
    async ensureCatalog() {
      if (this.catalog) return this.catalog;
      if (this.loading) return this.loading;
      this.loadButton.disabled = true;
      if (this.compact) {
        this.status.classList.add('gt-finder__sr-only');
        this.loadButton.hidden = true;
        const first = this.mode === 'engine' ? this.selects.engine : this.selects.make;
        first.replaceChildren(new Option(this.strings.loading, ''));
      }
      this.status.textContent = this.strings.loading;
      const started = performance.now();
      this.loading = loadCatalog(this.dataset.source, (loaded, total) => {this.status.textContent = this.strings.loading + ' ' + loaded + '/' + total;}, this.dataset.cacheKey).then(catalog => {
        this.catalog = catalog;
        this.dataset.loadMs = String(Math.round(performance.now() - started));
        this.dataset.catalogTotal = String(catalog.total);
        this.dataset.catalogRows = String(catalog.rows.length);
        this.dataset.invalidRows = String(catalog.products.reduce((sum, product) => sum + product.rows.filter(row => !validRow(row)).length, 0));
        this.fill('make', values(catalog.rows, 'make'));
        if (this.mode === 'engine') this.updateSelectors('mode');
        this.loadButton.hidden = true;
        this.status.textContent = this.strings.ready.replace('{count}', String(catalog.products.length));
        return catalog;
      }).catch(error => {
        this.dataset.loadError = error.message;
        if (this.compact) this.status.classList.remove('gt-finder__sr-only');
        this.status.textContent = this.strings.load_error;
        this.loadButton.disabled = false;
        this.loadButton.hidden = false;
        return null;
      }).finally(() => {this.loading = null;});
      return this.loading;
    }
    fill(name, options, preferred = '') {
      const select = this.selects[name];
      select.replaceChildren(new Option(this.strings.choose, ''));
      options.forEach(value => select.add(new Option(value, value)));
      select.disabled = !options.length;
      const exact = options.find(value => norm(value) === norm(preferred));
      if (exact) select.value = exact;
    }
    query() {
      return Object.fromEntries(Object.entries(this.selects).map(([name, select]) => [name, select.value]));
    }
    setMode(mode) {
      if (!['vehicle', 'engine', 'vin'].includes(mode)) return;
      this.mode = mode;
      if (this.compact) this.status.classList.add('gt-finder__sr-only');
      if (this.full) {
        const url = new URL(location.href);
        ['gt_make', 'gt_model', 'gt_year', 'gt_engine', 'gt_category'].forEach(key => url.searchParams.delete(key));
        url.searchParams.set('gt_mode', mode);
        history.replaceState(null, '', url.pathname + url.search);
      }
      this.querySelectorAll('[data-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === mode)));
      this.form.hidden = mode === 'vin';
      const vinForm = this.querySelector('[data-vin-form]');
      if (vinForm) vinForm.hidden = mode !== 'vin';
      ['make', 'model', 'year'].forEach(name => this.selects[name].parentElement.hidden = mode === 'engine');
      if (this.results) this.results.hidden = true;
      this.closeFilters(false);
      this.categoryList?.querySelectorAll('input').forEach(input => {input.checked = false;});
      if (mode !== 'vin') this.ensureCatalog().then(() => this.updateSelectors('mode'));
    }
    updateSelectors(changed) {
      if (!this.catalog) return;
      const query = this.query();
      if (this.mode === 'engine') {
        if (changed !== 'engine') this.fill('engine', values(this.catalog.rows, 'engine'), query.engine);
        this.submitButton.disabled = !this.selects.engine.value;
        if (this.results) this.results.hidden = true;
        return;
      }
      if (changed === 'make' || changed === 'mode') this.fill('model', values(this.catalog.rows.filter(row => norm(row.make) === norm(query.make)), 'model'), changed === 'mode' ? query.model : '');
      const modelRows = this.catalog.rows.filter(row => norm(row.make) === norm(this.selects.make.value) && norm(row.model) === norm(this.selects.model.value));
      if (['make', 'model', 'mode'].includes(changed)) {
        const years = new Set();
        modelRows.forEach(row => {for (let year = Number(row.from); year <= Number(row.to); year++) years.add(String(year));});
        this.fill('year', [...years].sort((a, b) => Number(b) - Number(a)), changed === 'mode' ? query.year : '');
      }
      if (changed !== 'engine') {
        const year = Number(this.selects.year.value);
        this.fill('engine', values(modelRows.filter(row => year >= Number(row.from) && year <= Number(row.to)), 'engine'), changed === 'mode' ? query.engine : '');
      }
      this.submitButton.disabled = !Object.values(this.query()).every(Boolean);
      if (this.results) this.results.hidden = true;
    }
    async search() {
      if (!await this.ensureCatalog()) return;
      const query = this.query();
      if (!query.engine || (this.mode !== 'engine' && !Object.values(query).every(Boolean))) {
        this.status.textContent = this.strings.choose_required;
        return;
      }
      const url = new URL(this.dataset.target, location.origin);
      url.searchParams.set('view', 'gt-vehicle-finder');
      url.searchParams.set('gt_mode', this.mode);
      Object.entries(query).forEach(([key, value]) => {if (value && (this.mode !== 'engine' || key === 'engine')) url.searchParams.set('gt_' + key, value);});
      if (!this.full) {location.assign(url.href); return;}
      history.replaceState(null, '', url.pathname + url.search);
      this.baseFoundProducts = find(this.catalog.products, query, this.mode).sort((a, b) => Number(b.available) - Number(a.available) || text(a.sku).localeCompare(text(b.sku), 'en', {numeric: true}));
      const preferredCategory = this.pendingCategory !== null ? this.pendingCategory : this.selectedCategories();
      this.pendingCategory = null;
      this.updateCategoryOptions(preferredCategory);
      this.querySelector('[data-result-subtitle]').textContent = this.mode === 'engine' ? query.engine : [query.make, query.model, query.year, query.engine].join(' · ');
      this.filterResults(true);
    }
    updateCategoryOptions(preferred) {
      if (!this.categoryList) return;
      const options = categoryOptions(this.baseFoundProducts, this.categoryDefinitions);
      const selected = new Set(categorySelection(preferred));
      this.categoryList.replaceChildren();
      options.forEach(category => {
        const row = this.querySelector('[data-category-row]').content.cloneNode(true);
        const input = row.querySelector('input');
        input.id = this.categoryList.id + '-' + category.handle;
        input.value = category.handle;
        input.checked = selected.has(category.handle);
        const label = row.querySelector('label');
        label.htmlFor = input.id;
        label.textContent = category.title + ' (' + category.count + ')';
        this.categoryList.append(row);
      });
      this.filterPanel.hidden = !options.length;
      this.querySelector('[data-filter-toolbar]').hidden = !options.length;
      this.querySelector('[data-result-layout]').classList.toggle('gt-finder__result-layout--filtered', !!options.length);
      this.syncFilterPanel();
    }
    selectedCategories() {
      return this.categoryList ? [...this.categoryList.querySelectorAll('input:checked')].map(input => input.value) : [];
    }
    filterResults(focus = false) {
      const selected = this.selectedCategories();
      const base = this.baseFoundProducts || [];
      this.foundProducts = filterCategory(base, selected, this.categoryDefinitions);
      if (this.categoryList) {
        this.querySelector('[data-clear-category]').hidden = !selected.length;
        this.querySelector('[data-category-count]').textContent = this.strings.category_count.replace('{shown}', String(this.foundProducts.length)).replace('{total}', String(base.length));
        this.querySelector('[data-filter-badge]').textContent = selected.length ? ' (' + selected.length + ')' : '';
        this.querySelector('[data-apply-filters]').textContent = this.strings.show_results.replace('{count}', String(this.foundProducts.length));
        const active = this.querySelector('[data-active-categories]');
        active.replaceChildren();
        selected.forEach(handle => {
          const category = this.categoryDefinitions.find(item => item.handle === handle);
          if (!category) return;
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.removeCategory = handle;
          button.textContent = category.title + ' ×';
          button.setAttribute('aria-label', this.strings.remove_category.replace('{category}', category.title));
          active.append(button);
        });
        active.hidden = !selected.length;
      }
      const url = new URL(location.href);
      if (selected.length) url.searchParams.set('gt_category', selected.join(','));
      else url.searchParams.delete('gt_category');
      history.replaceState(null, '', url.pathname + url.search);
      this.renderResults(1, focus);
    }
    syncFilterPanel() {
      if (!this.filterPanel) return;
      const mobile = this.filterMedia.matches;
      this.filterPanel.inert = mobile && !this.filtersOpen;
      if (mobile && this.filtersOpen) {
        this.filterPanel.setAttribute('role', 'dialog');
        this.filterPanel.setAttribute('aria-modal', 'true');
      } else {
        this.filterPanel.removeAttribute('role');
        this.filterPanel.removeAttribute('aria-modal');
      }
    }
    openFilters() {
      if (!this.filterMedia?.matches || this.filterPanel.hidden || this.filtersOpen) return;
      this.filtersOpen = true;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.filterPanel.classList.add('is-open');
      this.querySelector('[data-filter-overlay]').hidden = false;
      this.querySelector('[data-open-filters]').setAttribute('aria-expanded', 'true');
      this.syncFilterPanel();
      this.querySelector('[data-close-filters]').focus();
    }
    closeFilters(restoreFocus = true) {
      if (!this.filtersOpen) return;
      this.filtersOpen = false;
      document.body.style.overflow = this.previousOverflow;
      this.filterPanel.classList.remove('is-open');
      this.querySelector('[data-filter-overlay]').hidden = true;
      this.querySelector('[data-open-filters]').setAttribute('aria-expanded', 'false');
      this.syncFilterPanel();
      if (restoreFocus) this.querySelector('[data-open-filters]').focus({preventScroll: true});
    }
    filterKeydown(event) {
      if (!this.filtersOpen) return;
      if (event.key === 'Escape') {event.preventDefault(); this.closeFilters(); return;}
      if (event.key !== 'Tab') return;
      const controls = [...this.filterPanel.querySelectorAll('button,input,summary')].filter(element => !element.disabled && element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last.focus();}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first.focus();}
    }
    restoreQuery() {
      if (!this.catalog) return;
      const params = new URLSearchParams(location.search);
      if (!params.has('gt_engine')) return;
      this.pendingCategory = categorySelection(params.getAll('gt_category'));
      if (params.get('gt_mode') === 'engine') {
        this.setMode('engine');
        this.fill('engine', values(this.catalog.rows, 'engine'), params.get('gt_engine'));
      } else {
        this.fill('make', values(this.catalog.rows, 'make'), params.get('gt_make'));
        this.updateSelectors('make');
        this.selects.model.value = params.get('gt_model') || '';
        this.updateSelectors('model');
        this.selects.year.value = params.get('gt_year') || '';
        this.updateSelectors('year');
        this.selects.engine.value = params.get('gt_engine') || '';
      }
      this.updateSelectors('engine');
      if (!this.submitButton.disabled) this.search();
    }
    renderResults(page, focus = true) {
      const products = this.foundProducts || [];
      const totalPages = Math.max(1, Math.ceil(products.length / 24));
      this.resultPage = Math.max(1, Math.min(page, totalPages));
      const grid = this.querySelector('[data-results]');
      grid.replaceChildren();
      this.querySelector('[data-result-title]').textContent = products.length ? this.strings.matches.replace('{count}', String(products.length)) : this.strings.no_matches;
      if (!products.length) {
        const empty = document.createElement('p');
        empty.textContent = this.strings.empty_help;
        grid.append(empty);
      }
      products.slice((this.resultPage - 1) * 24, this.resultPage * 24).forEach(product => {
        const documentCard = new DOMParser().parseFromString(product.card, 'text/html');
        const card = documentCard.querySelector('.product-item');
        if (!card) return;
        card.querySelectorAll('script, .product-item__action-list, .product-item__swatch-list').forEach(element => element.remove());
        card.classList.add('gt-finder__native-card');
        card.dataset.productId = String(product.id);
        grid.append(card);
      });
      this.querySelector('[data-page]').textContent = this.strings.page.replace('{page}', String(this.resultPage)).replace('{pages}', String(totalPages));
      this.querySelector('[data-prev]').disabled = this.resultPage <= 1;
      this.querySelector('[data-next]').disabled = this.resultPage >= totalPages;
      this.querySelector('.gt-finder__pagination').hidden = products.length <= 24;
      this.results.hidden = false;
      if (focus) {
        this.results.focus({preventScroll: true});
        this.results.scrollIntoView({behavior: 'smooth', block: 'start'});
      }
    }
    async decodeVin() {
      const form = this.querySelector('[data-vin-form]');
      const status = this.querySelector('[data-vin-status]');
      const candidate = vinKind(form.elements.namedItem('vin').value);
      status.textContent = '';
      if (candidate.kind !== 'vin') {
        status.textContent = candidate.kind === 'chassis' ? this.strings.vin_short : this.strings.vin_invalid;
        return;
      }
      const button = form.querySelector('button');
      button.disabled = true;
      status.textContent = this.strings.vin_loading;
      try {
        const response = await fetch('https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/' + encodeURIComponent(candidate.vin) + '?format=json', {credentials: 'omit', signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw new Error('VIN unavailable');
        const vehicle = decodeFields(await response.json());
        if (!vehicle.make) {status.textContent = this.strings.vin_unknown; return;}
        if (norm(vehicle.make) !== 'ISUZU') {status.textContent = this.strings.vin_other.replace('{make}', vehicle.make); return;}
        if (!await this.ensureCatalog()) {status.textContent = this.strings.load_error; return;}
        this.setMode('vehicle');
        this.fill('make', values(this.catalog.rows, 'make'), vehicle.make);
        this.updateSelectors('make');
        const model = [...this.selects.model.options].find(option => norm(option.value) === norm(vehicle.model));
        if (model) this.selects.model.value = model.value;
        this.updateSelectors('model');
        this.selects.year.value = vehicle.year;
        this.updateSelectors('year');
        const engine = [...this.selects.engine.options].find(option => norm(option.value) === norm(vehicle.engine));
        if (engine) this.selects.engine.value = engine.value;
        this.updateSelectors('engine');
        const summary = [vehicle.make, vehicle.model, vehicle.year, vehicle.engine, vehicle.liters ? vehicle.liters + 'L' : '', vehicle.fuel].filter(Boolean).join(' · ');
        const message = summary + '. ' + (this.submitButton.disabled ? this.strings.vin_missing : this.strings.vin_confirm) + (vehicle.error && vehicle.error !== '0' ? ' ' + this.strings.vin_uncertain : '');
        if (this.compact) this.status.classList.remove('gt-finder__sr-only');
        this.status.textContent = message;
        status.textContent = message;
      } catch (error) {
        status.textContent = this.strings.vin_error;
      } finally {button.disabled = false;}
    }
  }
  window.customElements.define('gt-vehicle-finder', VehicleFinder);
})();

